import { addWindowGlobalVariableTyped, getWindowGlobalVariableTyped } from "@regrello/core-utils";
import type { FeatureFlagKey } from "@regrello/feature-flags-api";
import { consoleWarnInDevelopmentModeOnly } from "../utils/environmentUtils";
import { RegrelloRestApiService } from "./RegrelloRestApiService";

/**
 * The default value used for feature flags that can't be evaluated for whatever reason.
 */
const DEFAULT_IS_ENABLED_VALUE = false;

/**
 * A singleton that stores feature flag overrides for testing purposes.
 */
const featureFlagMockOverrides: Record<string, boolean> = {};

/**
 * A service that we can use to check whether particular feature flags are enabled. Also provides
 * mechanisms for mocking feature flags in unit tests.
 *
 * @example
 * import { FeatureFlagService } from "relative/path/to/FeatureFlagService";
 * // ...
 * if (FeatureFlagService.isEnabled(FeatureFlagKey.MY_FEATURE)) {
 *   return <MyFeature />;
 * } else {
 *   return <OldFeature />;
 * }
 */
// (clewis): Feature flag states are served via the `/feature-flags` endpoint and, once fetched, are
// read from the `globalThis.regrelloAllFeatureFlagValues` object. Feature flags are initialized on
// app load via `FeatureFlagServiceInitializer` and refreshed when the workspace changes via
// `FeatureFlagRefetcher`.
export const FeatureFlagService = {
  /**
   * Initializes feature flags by fetching them from the backend and updating the global window object.
   * This should be called on app initialization to load feature flags for the current workspace.
   *
   * @param workspaceShortUuid - Optional workspace short UUID to include in the request query parameter.
   * @param signal - Optional AbortSignal to cancel the request if the workspace changes.
   * @returns A promise that resolves when feature flags have been fetched and set on the window object.
   */
  init: async (workspaceShortUuid: string | null): Promise<void> => {
    const response = await RegrelloRestApiService.getFeatureFlags(workspaceShortUuid);
    if (response.status !== 200) {
      throw new Error(`Failed to fetch feature flags: ${response.status}`);
    }

    const featureFlags = response.json as Record<FeatureFlagKey, boolean>;

    addWindowGlobalVariableTyped("regrelloAllFeatureFlagValues" as const, featureFlags);
    addWindowGlobalVariableTyped("regrelloFeatureFlagWorkspaceUuid" as const, workspaceShortUuid ?? null);
  },

  /**
   * Returns `true` if feature flags have been initialized for the given workspace UUID, or `false`
   * otherwise. Passing `null` will return `true` if feature flags were last initialized without a
   * workspace UUID.
   *
   * @param workspaceShortUuid - The short UUID of the workspace to check.
   * @returns `true` if feature flags have been initialized for the given workspace UUID, or `false`
   * otherwise.
   */
  isInitialized: (workspaceShortUuid: string | null): boolean => {
    return getWindowGlobalVariableTyped("regrelloFeatureFlagWorkspaceUuid" as const) === workspaceShortUuid;
  },

  /**
   * Returns `true` if the specified feature flag is currently enabled for the current workspace, or
   * `false` otherwise.
   *
   * @param featureFlagKey - feature flag key defined in Launch Darkly
   * @param mockGet - optional function to get the feature flag value from a mock object in unit tests
   * @returns whether or not the viewer has the feature flag enabled
   */
  isEnabled: (
    featureFlagKey: FeatureFlagKey,
    mockGet?: (featureFlagKey: FeatureFlagKey) => boolean | null | undefined,
  ): boolean => {
    // Check for flag overrides (now used only for unit testing).
    if (featureFlagKey in featureFlagMockOverrides) {
      return featureFlagMockOverrides[featureFlagKey];
    }

    // Read from regrelloAllFeatureFlagValues (set by FeatureFlagServiceInitializer on app initialization).
    const isEnabled =
      mockGet != null
        ? mockGet(featureFlagKey)
        : getWindowGlobalVariableTyped("regrelloAllFeatureFlagValues" as const)?.[featureFlagKey];

    if (isEnabled == null) {
      consoleWarnInDevelopmentModeOnly(
        `Feature flag "${featureFlagKey}" not found in config. Returning default value of ${DEFAULT_IS_ENABLED_VALUE}. ` +
          "If this is a frontend-only feature flag, make sure you've added it to flags.go on the backend.",
      );
      return DEFAULT_IS_ENABLED_VALUE;
    }

    return isEnabled;
  },

  /**
   * For tests only: set a feature flag to a specific value.
   *
   * @param featureFlagKey - feature flag key defined in Launch Darkly
   * @param isEnabled - whether or not the feature flag is enabled
   */
  mockEnabled: (featureFlagKey: FeatureFlagKey, isEnabled: boolean) => {
    featureFlagMockOverrides[featureFlagKey] = isEnabled;
  },

  /**
   * For tests only: reset a feature flag to its default value.
   *
   * @param featureFlagKey - feature flag key defined in Launch Darkly
   */
  mockReset: (featureFlagKey: FeatureFlagKey) => {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete featureFlagMockOverrides[featureFlagKey];
  },
};
